/** The 6502's documented instruction set as data (spec 07 §3), decoded once into lookup tables. */

/** Mnemonic, then `opcode mode cycles` per addressing mode (base timings, NMOS datasheet). */
const TABLE = `ADC 69 imm 2,65 zp 3,75 zpx 4,6D abs 4,7D abx 4,79 aby 4,61 izx 6,71 izy 5
AND 29 imm 2,25 zp 3,35 zpx 4,2D abs 4,3D abx 4,39 aby 4,21 izx 6,31 izy 5
ASL 0A acc 2,06 zp 5,16 zpx 6,0E abs 6,1E abx 7|BIT 24 zp 3,2C abs 4|BRK 00 imp 7
BCC 90 rel 2|BCS B0 rel 2|BEQ F0 rel 2|BMI 30 rel 2|BNE D0 rel 2|BPL 10 rel 2|BVC 50 rel 2|BVS 70 rel 2
CLC 18 imp 2|CLD D8 imp 2|CLI 58 imp 2|CLV B8 imp 2|SEC 38 imp 2|SED F8 imp 2|SEI 78 imp 2
CMP C9 imm 2,C5 zp 3,D5 zpx 4,CD abs 4,DD abx 4,D9 aby 4,C1 izx 6,D1 izy 5
CPX E0 imm 2,E4 zp 3,EC abs 4|CPY C0 imm 2,C4 zp 3,CC abs 4
DEC C6 zp 5,D6 zpx 6,CE abs 6,DE abx 7|DEX CA imp 2|DEY 88 imp 2
EOR 49 imm 2,45 zp 3,55 zpx 4,4D abs 4,5D abx 4,59 aby 4,41 izx 6,51 izy 5
INC E6 zp 5,F6 zpx 6,EE abs 6,FE abx 7|INX E8 imp 2|INY C8 imp 2
JMP 4C abs 3,6C ind 5|JSR 20 abs 6|RTI 40 imp 6|RTS 60 imp 6|NOP EA imp 2
LDA A9 imm 2,A5 zp 3,B5 zpx 4,AD abs 4,BD abx 4,B9 aby 4,A1 izx 6,B1 izy 5
LDX A2 imm 2,A6 zp 3,B6 zpy 4,AE abs 4,BE aby 4|LDY A0 imm 2,A4 zp 3,B4 zpx 4,AC abs 4,BC abx 4
LSR 4A acc 2,46 zp 5,56 zpx 6,4E abs 6,5E abx 7|ROL 2A acc 2,26 zp 5,36 zpx 6,2E abs 6,3E abx 7
ROR 6A acc 2,66 zp 5,76 zpx 6,6E abs 6,7E abx 7
ORA 09 imm 2,05 zp 3,15 zpx 4,0D abs 4,1D abx 4,19 aby 4,01 izx 6,11 izy 5
PHA 48 imp 3|PHP 08 imp 3|PLA 68 imp 4|PLP 28 imp 4
SBC E9 imm 2,E5 zp 3,F5 zpx 4,ED abs 4,FD abx 4,F9 aby 4,E1 izx 6,F1 izy 5
STA 85 zp 3,95 zpx 4,8D abs 4,9D abx 5,99 aby 5,81 izx 6,91 izy 6|STX 86 zp 3,96 zpy 4,8E abs 4
STY 84 zp 3,94 zpx 4,8C abs 4|TAX AA imp 2|TAY A8 imp 2|TSX BA imp 2|TXA 8A imp 2|TXS 9A imp 2|TYA 98 imp 2`;

/** Addressing modes, by index into `MODES`. */
export const IMP = 0;
export const ACC = 1;
export const IMM = 2;
export const ZP = 3;
export const ZPX = 4;
export const ZPY = 5;
export const ABS = 6;
export const ABX = 7;
export const ABY = 8;
export const IZX = 9;
export const IZY = 10;
export const IND = 11;
export const REL = 12;
export const MODES = ['imp', 'acc', 'imm', 'zp', 'zpx', 'zpy', 'abs', 'abx', 'aby', 'izx', 'izy', 'ind', 'rel'] as const;
/** Reads that take one more cycle when indexing crosses a page. */
const PAGE_PENALTY = new Set(['ADC', 'AND', 'CMP', 'EOR', 'LDA', 'LDX', 'LDY', 'ORA', 'SBC']);

export const OP_MNE: Array<string | undefined> = new Array<string | undefined>(256).fill(undefined);
export const OP_MODE = new Uint8Array(256);
export const OP_CYCLES = new Uint8Array(256);
export const OP_PENALTY = new Uint8Array(256);
for (const entry of TABLE.split(/[|\n]/)) {
  const mne = entry.slice(0, 3);
  for (const form of entry.slice(4).split(',')) {
    const [code = '', mode = '', cycles = '0'] = form.split(' ');
    const op = Number.parseInt(code, 16);
    OP_MNE[op] = mne;
    OP_MODE[op] = MODES.indexOf(mode as (typeof MODES)[number]);
    OP_CYCLES[op] = Number.parseInt(cycles, 10);
    OP_PENALTY[op] = PAGE_PENALTY.has(mne) ? 1 : 0;
  }
}

export const C = 0x01;
export const Z = 0x02;
export const I = 0x04;
export const D = 0x08;
export const B = 0x10;
export const U = 0x20;
export const V = 0x40;
export const N = 0x80;

export const hex = (v: number, digits: number): string => v.toString(16).toUpperCase().padStart(digits, '0');
